import { memo } from 'react';

import { usePublicBoardPage } from '~/components/Layouts/PublicBoardLayout/hooks/usePublicBoardPage';
import { PublicBoard } from '~/components/v2/components/board/PublicBoard';
import { usePublicShowcaseBoardSavedSort } from '~/components/v2/components/showcase/hooks/usePublicShowcaseBoardSavedSort';
import { usePublicShowcaseHomeBreadcrumb } from '~/components/v2/components/showcase/hooks/usePublicShowcaseHomeBreadcrumb';
import { PublicShowcaseBoardBreadcrumbs } from '~/components/v2/components/showcase/PublicShowcaseBoardBreadcrumbs';
import { PublicShowcaseBoardGalleryView } from '~/components/v2/components/showcase/PublicShowcaseBoardGalleryView';
import { PublicShowcaseBoardTableView } from '~/components/v2/components/showcase/PublicShowcaseBoardTableView';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

export type PublicShowcaseBoardViewProps = {
  /** The board being viewed inside the collection. Guaranteed non-null by the caller's boardId gate. */
  boardId: string;
};

/**
 * The collection-owned view of a board rendered INSIDE a collection link — the one board view for
 * BOTH the real public collection-board route (`/a/{shortId}/b/{boardId}`) and the collection editor's
 * inline edit canvas ("navigate + preview" WYSIWYG of FOUND-7). A collection route always mounts ONE
 * collection tree; a board drilled into from it renders here, composing the shared PublicBoard with
 * ZERO collection logic inside the shared board leaves. This replaces the old approach where the
 * collection-board route swapped in a second PublicBoardLayout with `objectType='collection'` threaded
 * down — the disease that pushed "am I in a collection?" checks into board leaves and mounted dual
 * provider/reCAPTCHA stacks.
 *
 * The board fetches its OWN record here — a deep-linked descendant contributes no SSR payload (the
 * clip-in-board precedent: the owning tree loads its children async; SSR seeds only the route's own
 * object). `usePublicBoardPage`'s queryFn dispatches the fetched board into the centralizedBoard
 * redux slice PublicBoard renders from; until it resolves, the board chrome shows its loading state.
 *
 * The board data layer is `shortId` + board-id scoped and objectType-agnostic, so this reuses the
 * public board subtree wholesale. Everything PublicBoard needs — the centralizedBoard redux slice,
 * permissions, ShortIdContext, scroll/selection/thumbnail/custom-field providers, and the config view
 * (established by PublicShowcasePage's usePublicBoardlessConfigView) — is already mounted by the
 * collection tree, so only the board record has to be fetched here. View type, card size, and
 * thumbnail style still come from the link; sort comes from the board's saved view (FOUND-872)
 * because creators expect Custom Sort (and other saved sorts) to carry into the showcase board.
 * Do not apply the full board.views via setSavedConfigurableViewsAction — that would replace
 * gallery vs table / card size with the workspace board view.
 *
 * Breadcrumbs come from the collection area's own builder (PublicShowcaseBoardBreadcrumbs), which
 * prepends the collection + sub-page crumbs and owns the host-vs-route navigation branches. No
 * trailing "Save all" download menu is supplied: that is a standalone-board viewer action reading
 * PublicRootBoardProvider, which the collection tree never mounts.
 */
export const PublicShowcaseBoardView = memo(({ boardId }: PublicShowcaseBoardViewProps) => {
  const { onNavigateToBoard } = usePublicShowcaseContext();
  const { showcaseHomeBreadcrumb } = usePublicShowcaseHomeBreadcrumb();

  const { data: board } = usePublicBoardPage({ boardId, initialData: undefined });
  usePublicShowcaseBoardSavedSort({ board });

  return (
    <PublicBoard
      // The drilled board's asset modal leads its trail with the collection-home crumb — built on
      // the collection tree's own navigation, supplied here so no board-tree file reads collection
      // data. Absent on the standalone board link, whose trail starts at the board's ancestors.
      assetModalBreadcrumbPrefix={showcaseHomeBreadcrumb}
      breadcrumbs={<PublicShowcaseBoardBreadcrumbs />}
      // The collection surface's items views: fetch is the layout contract's board-drill hooks
      // (ShortUrl on `/a`, workspace in Edit + Preview); cards ride the same contract.
      GalleryView={PublicShowcaseBoardGalleryView}
      // The collection tree's own drill action cascades into the drilled board's contract.
      onNavigateToBoard={onNavigateToBoard}
      TableView={PublicShowcaseBoardTableView}
    />
  );
});

PublicShowcaseBoardView.displayName = 'PublicShowcaseBoardView';
